import { Dispatch } from 'react';

import type { UploadFile } from 'antd';
import { StatusCodes } from 'http-status-codes';

import { catchErrorProps } from 'hooks/useErrorCatch';

import { postFormData } from 'utils/api/api';

import { AttachmentTypes } from 'types/api';

import { RESOURCE } from 'constants/endpoints';
import { GlobalModules } from 'constants/enums';
import { NO_USER_PERMISSION_MESSAGE_TEXT } from 'constants/notifications';
import { TFunction } from 'i18next';

interface uploadFileProps {
  module: GlobalModules;
  file: UploadFile;
  attachmentType: AttachmentTypes;
  ownerId: string;
  isEditable: boolean;
  catchError: (props: catchErrorProps) => void;
  childId?: string;
  setIsDisabled?: Dispatch<React.SetStateAction<boolean>>;
  onAddFile?: () => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  translate: TFunction<'common', undefined>
}

interface AttachmentInfo {
  module: GlobalModules;
  type: AttachmentTypes;
  file_name: string;
  owner_id: string;
  child_id?: string;
}

export const uploadFile = async ({
  module,
  file,
  attachmentType,
  ownerId,
  childId,
  isEditable = false,
  catchError,
  setIsDisabled,
  onAddFile,
  onUploadStart,
  onUploadEnd,
  translate
}: uploadFileProps): Promise<void> => {
  const formData = new FormData();
  const attachment_info: AttachmentInfo = {
    module: module,
    type: attachmentType,
    file_name: file?.name,
    owner_id: ownerId
  };
  if (childId) {
    attachment_info.child_id = childId;
  }
  formData.append('attachment', file as unknown as Blob);
  formData.append(
    'attachment_info',
    new Blob([JSON.stringify(attachment_info)], { type: 'application/json' })
  );

  try {
    if (onUploadStart) {
      onUploadStart();
    }
    if (setIsDisabled) {
      setIsDisabled(true);
    }

    await postFormData(RESOURCE, formData);

    if (setIsDisabled) {
      setIsDisabled(!isEditable);
    }
    if (onAddFile) {
      onAddFile();
    }
  } catch (error) {
    catchError({
      error,
      default: {
        message: translate('error.message.uploadingFile')
      },
      additionalErrorMessageMap: {
        [StatusCodes.FORBIDDEN]: { message: translate(NO_USER_PERMISSION_MESSAGE_TEXT) }
      }
    });
    if (onUploadEnd) {
      onUploadEnd();
    }
    if (setIsDisabled) {
      setIsDisabled(false);
    }
  }
};
